import React, { useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { setAndrologyData } from '../../../../actions/andrologyAction';
import { getNestedState } from '../utils/index';
import CustomTextField from '../../../../common/CustomTextField';
import { units } from '../../../../constants/units';
import { renderDropdownWithoutLabel } from '../../../../common/inputField';

const CyroFreezing = ({ keyPath }) => {
  const dispatch = useDispatch();
  const cryopreservation = useSelector((state) =>
    getNestedState(state.andrologyReducer, keyPath),
  );

  const andrologyReducer = useSelector((state) => state.andrologyReducer);

  const handleChange = (key, value) => {
    dispatch(setAndrologyData(`${keyPath}.${key}`, value));
    if (key === 'freezingDuration.years' || key === 'freezingDuration.months') {
      const updatedValue = value || 0;
      let years =
        key === 'freezingDuration.years'
          ? updatedValue
          : cryopreservation.freezingDuration.years || 0;
      let months =
        key === 'freezingDuration.months'
          ? updatedValue
          : cryopreservation.freezingDuration.months || 0;
      years = parseInt(years, 10);
      months = parseInt(months, 10);
      const totalMonths = years * 12 + months;
      const date = new Date();
      date.setMonth(date.getMonth() + totalMonths);
      handleChange('dateOfExpiry', date.toISOString().split('T')[0]);
      const freezingInventory = andrologyReducer.sfr.freezingInventory;

      freezingInventory.freezingDurationYears = years;
      freezingInventory.freezingDurationMonths = months;
      freezingInventory.dateOfExpiry = date.toISOString().split('T')[0];
      dispatch(setAndrologyData(`sfr.freezingInventory`, freezingInventory));
    }

    if (key === 'noOfVialsStored') {
      const copyRows = [
        ...andrologyReducer.sfr.freezingInventory.CryopreservationDetails,
      ];
      copyRows[0].numberOfVialsStored = value;
      const date = new Date();
      copyRows[0].dateOfCryopreservation = date.toISOString().split('T')[0];
      dispatch(
        setAndrologyData(
          `sfr.freezingInventory.CryopreservationDetails`,
          copyRows,
        ),
      );
    }

    if (key === 'dateOfExpiry') {
      dispatch(setAndrologyData(`sfr.freezingInventory.dateOfExpiry`, value));
    }
  };

  const handleDropdownChange = (e, index, name) => {
    const { value } = e.target;
    handleChange(name, value);
  };

  return (
    <Box flex={1}>
      <Paper className="p-3 mb-3">
        <Typography variant="h6" gutterBottom>
          Cryopreservation Details
        </Typography>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>Semen Cryopreserved</TableCell>

                <TableCell>
                  {renderDropdownWithoutLabel(
                    'semenCryoPreserved',
                    cryopreservation.semenCryoPreserved,
                    ['Yes', 'No'],
                    handleDropdownChange,
                  )}
                </TableCell>

                <TableCell>Purpose</TableCell>
                <TableCell>
                  {renderDropdownWithoutLabel(
                    'purpose',
                    cryopreservation.purpose,
                    ['Back-up Freezing', 'Fertility Preservation', 'Other'],
                    handleDropdownChange,
                  )}

                  {cryopreservation.purpose === 'Other' && (
                    <CustomTextField
                      value={cryopreservation.purposeOther}
                      onChange={(e) =>
                        handleChange('purposeOther', e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>No. of Vials Stored</TableCell>
                <TableCell>
                  <CustomTextField
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    value={cryopreservation.noOfVialsStored}
                    onChange={(e) =>
                      handleChange('noOfVialsStored', e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </TableCell>
                <TableCell>Tank No.</TableCell>
                <TableCell>
                  <CustomTextField
                    type="text"
                    onWheel={(e) => e.target.blur()}
                    value={cryopreservation.tankNo}
                    onChange={(e) => handleChange('tankNo', e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Canister No.</TableCell>
                <TableCell>
                  <CustomTextField
                    value={cryopreservation.canisterNo}
                    onChange={(e) => handleChange('canisterNo', e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </TableCell>
                <TableCell>Cane</TableCell>
                <TableCell>
                  <CustomTextField
                    value={cryopreservation.cane}
                    onChange={(e) => handleChange('cane', e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>Freezing Duration</TableCell>
                <TableCell>
                  <div className="row-flex">
                    <CustomTextField
                      type="number"
                      label="Years"
                      value={cryopreservation.freezingDuration?.years}
                      onChange={(e) =>
                        handleChange('freezingDuration.years', e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      onWheel={(e) => e.target.blur()}
                      fullWidth
                      inputProps={{ min: 0 }}
                    />
                    <CustomTextField
                      type="number"
                      label="Months"
                      inputProps={{ min: 0, max: 12 }}
                      value={cryopreservation.freezingDuration?.months}
                      onChange={(e) =>
                        handleChange('freezingDuration.months', e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      onWheel={(e) => e.target.blur()}
                      fullWidth
                    />
                  </div>
                </TableCell>
                <TableCell>Date of Expiry</TableCell>
                <TableCell>
                  <CustomTextField
                    type="date"
                    value={cryopreservation.dateOfExpiry}
                    onChange={(e) =>
                      handleChange('dateOfExpiry', e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Sample Shifted By</TableCell>
                <TableCell>
                  <CustomTextField
                    value={cryopreservation.samepleShiftedBy}
                    onChange={(e) =>
                      handleChange('samepleShiftedBy', e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </TableCell>
                <TableCell>Witness</TableCell>
                <TableCell>
                  <CustomTextField
                    value={cryopreservation.witness}
                    onChange={(e) => handleChange('witness', e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default CyroFreezing;
